import { defineComponent as _defineComponent } from 'vue'
import AddOrConvertCurrencyButtons from './AddOrConvertCurrencyButtons.vue';
import CollateralCurrencyTransactionForm from './CollateralCurrencyTransactionForm.vue';
import FormattedCurrency from '../FormattedCurrency.vue';
import { BModal } from 'bootstrap-vue';
import { CollateralTransactionState, Wallet } from 'ah-api-gateways';
import { Observable, from, combineLatest } from 'rxjs';
import { VButton, VCol, VRow } from 'ah-common-lib/src/common/components';
import { capitalize } from 'lodash';
import { computed, PropType, ref, watch } from 'vue';
import { resetForm } from 'ah-common-lib/src/form/helpers';
import { mergeMap } from 'rxjs/operators';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useToast } from 'ah-common-lib/src/toast';
import { useTradeState } from 'ah-trades';
import { waitForEntityChange, HttpError } from 'ah-requests';
import { ManageCollateralActionType, ManageCollateralCurrenciesState } from '../interfaces';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';


export default /*#__PURE__*/_defineComponent({
  __name: 'ManageCollateralEditor',
  props: {
  activeMarginCall: {
    type: Boolean,
    required: true,
  },
  availableCollateral: {
    type: Number,
    required: true,
  },
  currenciesState: {
    type: Object as PropType<ManageCollateralCurrenciesState>,
    required: true,
  },
  disablePostCollateral: {
    type: Boolean,
    required: true,
  },
  isPartnerUser: {
    type: Boolean,
    required: true,
  },
  ownerId: {
    type: String,
    required: true,
  },
  isCreditProfileUtilisationExceeded: {
    type: Boolean,
    required: true,
  },
},
  emits: ["reload-collateral", "action-changed"],
  setup(__props, { emit }: { emit: ({
  /*
   * reload-collateral - emitted when the transaction has been executed successfully to reload again the collateral values
   */
  (e: 'reload-collateral'): void;
  /*
   * action-changed - inform the parent of which radio button is selected
   */
  (e: 'action-changed', value: ManageCollateralActionType): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const requestManager = useRequestManager();

const toast = useToast();

const tradeState = useTradeState();

const onBehalfOfClient = useOnBehalfOf();

const completeActionModal = ref<InstanceType<typeof BModal> | null>(null);

const selectedActionType = ref<ManageCollateralActionType>('withdraw');

const totalEquivalent = computed(() => {
  return props.currenciesState.currencies.reduce((total, { currency, model }) => {
    const rate = props.currenciesState.rates?.[currency.toLowerCase()] ?? 0;
    const amount = model?.amount ?? 0;
    return total + amount * rate;
  }, 0);
});

const isOverWithdrawing = computed(
  () => selectedActionType.value === 'withdraw' && totalEquivalent.value > props.availableCollateral
);

const disableWithdrawCollateral = computed(
  () => props.activeMarginCall || props.isCreditProfileUtilisationExceeded || props.availableCollateral <= 0
);

const isFormInvalid = computed(() => {
  if (totalEquivalent.value === 0 || isOverWithdrawing.value) {
    return true;
  }

  if (selectedActionType.value === 'withdraw' && props.activeMarginCall) {
    return true;
  }

  return props.currenciesState.currencies.some((currencyObj) => {
    const { currency, model, validation } = currencyObj;

    if (validation?.$invalid) {
      return true;
    }

    if (
      model &&
      model.amount > 0 &&
      !tradeState.store.useWalletsStore().getWallet(currency, { isPartner: props.isPartnerUser, id: props.ownerId })
    ) {
      return true;
    }

    return false;
  });
});

const toastMessagePrefix = computed(
  () => `${selectedActionType.value === 'post' ? 'Posted to' : 'Withdrew from'} collaterals`
);

const TX_ERROR = Symbol('Collateral Transaction has failed');

function performTransaction() {
  const requests: Observable<Wallet | null>[] = [];

  props.currenciesState.currencies.forEach((c) => {
    if (c.model && c.model.amount > 0) {
      const wallet = tradeState.store.useWalletsStore().getWallet(c.currency, {
        isPartner: props.isPartnerUser,
        id: props.ownerId,
      });

      const payload: { amount: number; currency: string } = { amount: c.model.amount, currency: c.currency };

      if (wallet && props.ownerId) {
        const request =
          selectedActionType.value === 'withdraw'
            ? tradeState.services.trade.withdrawCollateral(props.ownerId, payload, onBehalfOfClient.value?.id)
            : tradeState.services.trade.postCollateral(props.ownerId, payload, onBehalfOfClient.value?.id);

        requests.push(
          request.pipe(
            mergeMap((collateralTxVersionedObject) =>
              waitForEntityChange(
                () => tradeState.services.trade.getCollateralTransaction(collateralTxVersionedObject.id),
                (collateral) => {
                  if (collateral.state === CollateralTransactionState.FAILED) {
                    throw TX_ERROR;
                  }

                  return collateral.state === CollateralTransactionState.COMPLETED;
                },
                {
                  ignoreErrors(e: HttpError) {
                    return e.response?.status === 404;
                  },
                }
              )
            ),
            mergeMap(() =>
              from(
                tradeState.store.useWalletsStore().waitForWalletChange({
                  id: wallet.id,
                })
              )
            )
          )
        );
      }
    }
  });

  return requestManager.manager.new('performTransaction', combineLatest(requests)).subscribe({
    complete: () => {
      toast.success(`${toastMessagePrefix.value} successfully.`);

      emit('reload-collateral');

      props.currenciesState.currencies.forEach((currencyObj) => {
        currencyObj.model = { amount: 0 };
        if (currencyObj.validation) {
          resetForm(currencyObj.validation);
        }
      });
    },
    error: (err: any) => {
      if (err === TX_ERROR) {
        toast.error(`${toastMessagePrefix.value} failed.`);
      } else {
        toast.error('An unexpected problem has occurred. Please try again later.');
      }
    },
  });
}

function openModal() {
  completeActionModal.value?.show();
}

function onClick() {
  if (onBehalfOfClient.value) {
    openModal();
  } else {
    performTransaction();
  }
}

function onActionTypeClick(event: any) {
  selectedActionType.value = event.target.value;
}

watch(selectedActionType, () => emit('action-changed', selectedActionType.value), { immediate: true });

watch(
  () => disableWithdrawCollateral.value,
  () => {
    if (disableWithdrawCollateral.value && !props.disablePostCollateral) {
      selectedActionType.value = 'post';
    }
  },
  { immediate: true }
);

return { __sfc: true,props, emit, requestManager, toast, tradeState, onBehalfOfClient, completeActionModal, selectedActionType, totalEquivalent, isOverWithdrawing, disableWithdrawCollateral, isFormInvalid, toastMessagePrefix, TX_ERROR, performTransaction, openModal, onClick, onActionTypeClick, AddOrConvertCurrencyButtons, CollateralCurrencyTransactionForm, FormattedCurrency, BModal, VButton, VCol, VRow, capitalize, formatCurrencyValue }
}

})